import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { mandalApi } from '../api/dashboard';
import { exportsApi } from '../api/modules';
import { Drawer, DrawerCloseButton } from '../components/ui/Drawer';
import { useToast } from '../components/toast/Toast';

interface Props {
  open: boolean;
  code: string | null;
  onClose: () => void;
}

export function MandalDrawer({ open, code, onClose }: Props) {
  const { show } = useToast();
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    enabled: !!code,
    queryKey: ['mandal', code],
    queryFn: () => mandalApi.detail(code!),
  });

  return (
    <Drawer open={open && !!code} onClose={onClose}>
      <div className="bg-navy-800 text-white px-6 py-5 flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">Mandal drill-down</div>
          <h2 className="serif-display text-2xl font-medium mt-1">
            {data ? data.name : code ?? 'Loading…'}
          </h2>
          {data && (
            <div className="text-sm opacity-90 mt-2 font-numeric">
              {data.districts_count} districts · {data.beneficiaries.toLocaleString('en-IN')} beneficiaries
            </div>
          )}
        </div>
        <DrawerCloseButton onClick={onClose} />
      </div>

      <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
        {isLoading || !data ? (
          <div className="space-y-3">
            <div className="h-20 bg-cream-100 rounded-sm animate-pulse" />
            <div className="h-32 bg-cream-100 rounded-sm animate-pulse" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Beneficiaries</div>
                <div className="font-numeric serif-display text-xl text-navy-800 font-medium mt-1">
                  {data.beneficiaries.toLocaleString('en-IN')}
                </div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Disbursed</div>
                <div className="font-numeric serif-display text-xl text-navy-800 font-medium mt-1">
                  ₹{data.disbursed_cr.toFixed(2)} Cr
                </div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">In pipeline</div>
                <div className="font-numeric serif-display text-xl text-navy-800 font-medium mt-1">
                  {data.pipeline.toLocaleString('en-IN')}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-navy-800 mb-2">District breakdown</h3>
              <div className="border border-cream-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-cream-100">
                      <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">District</th>
                      <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Beneficiaries</th>
                      <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Disbursed (₹L)</th>
                      <th className="text-right px-3 py-2 text-[11px] uppercase tracking-wider text-navy-800/70 font-semibold">Alerts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.districts.map((d) => (
                      <tr key={d.name} className="border-t border-cream-100 hover:bg-cream-50">
                        <td className="px-3 py-2 font-medium text-navy-800">{d.name}</td>
                        <td className="px-3 py-2 text-right font-numeric">{d.beneficiaries.toLocaleString('en-IN')}</td>
                        <td className="px-3 py-2 text-right font-numeric">{d.disbursed_lakhs.toFixed(1)}</td>
                        <td className={`px-3 py-2 text-right font-numeric ${d.open_alerts > 2 ? 'text-red-600 font-medium' : 'text-navy-800/70'}`}>
                          {d.open_alerts}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {Object.keys(data.scheme_split).length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-navy-800 mb-2">Scheme split</h3>
                <div className="space-y-2">
                  {Object.entries(data.scheme_split).map(([code, count]) => {
                    const total = Object.values(data.scheme_split).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={code}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-navy-800/80">{code}</span>
                          <span className="font-numeric font-medium text-navy-800">{pct}%</span>
                        </div>
                        <div className="h-2 bg-cream-100 rounded-sm overflow-hidden">
                          <div
                            className={`h-full ${code === 'GOAT' ? 'bg-navy-800' : code === 'SHEEP' ? 'bg-saffron-500' : 'bg-navy-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t border-cream-200 px-6 py-4 flex items-center gap-2 bg-cream-50">
        <button
          type="button"
          disabled={exporting || !code}
          onClick={async () => {
            if (!code) return;
            setExporting(true);
            show('Generating mandal PDF…');
            try {
              const ec = await exportsApi.download(`/exports/pdf/mandal/${code}`, `mandal-${code}.pdf`);
              show(`PDF downloaded · ${ec}`);
            } catch {
              show('Export failed');
            } finally {
              setExporting(false);
            }
          }}
          className="flex-1 bg-navy-800 hover:bg-navy-700 disabled:opacity-60 text-white text-sm py-2.5 rounded-sm"
        >
          {exporting ? 'Generating…' : 'Export mandal report'}
        </button>
        <button
          type="button"
          onClick={() => show('Review meeting scheduled · Calendar invite sent')}
          className="px-4 py-2.5 border border-saffron-500 text-saffron-600 hover:bg-saffron-50 text-sm rounded-sm"
        >
          Schedule review
        </button>
      </div>
    </Drawer>
  );
}
